import React from 'react';
import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X } from 'lucide-react';
import { Schedule, Debt } from '../types';

interface CreditCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  householdId: string;
  editingCard?: Debt | null;
}

export default function CreditCardModal({ isOpen, onClose, userId, householdId, editingCard }: CreditCardModalProps) {
  const [name, setName] = useState('');
  const [totalAmount, setTotalAmount] = useState(''); // Credit Limit
  const [currentAmount, setCurrentAmount] = useState(''); // Current Balance
  const [monthlyMinimum, setMonthlyMinimum] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [statementDate, setStatementDate] = useState('1');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingCard && isOpen) {
      setName(editingCard.name);
      setTotalAmount(editingCard.totalAmount.toString());
      setCurrentAmount(editingCard.currentAmount.toString());
      setMonthlyMinimum(editingCard.monthlyMinimum.toString());
      setInterestRate(editingCard.interestRate.toString());
      if (editingCard.statementDate) setStatementDate(editingCard.statementDate.toString());
    } else if (isOpen) {
      setName('');
      setTotalAmount('');
      setCurrentAmount('');
      setMonthlyMinimum('');
      setInterestRate('');
      setStatementDate('1');
    }
  }, [editingCard, isOpen]);

  // Auto-calculate Canadian minimum payment for credit cards
  useEffect(() => {
    const balance = Number(currentAmount) || 0;
    const rate = Number(interestRate) || 0;
    if (balance > 0) {
      const monthlyInterest = balance * (rate / 100) / 12;
      let calcMin = monthlyInterest + 10;
      if (calcMin > balance) calcMin = balance;
      setMonthlyMinimum(calcMin.toFixed(2));
    } else {
      setMonthlyMinimum('0');
    }
  }, [currentAmount, interestRate]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totalAmount || !monthlyMinimum || !interestRate) return;
    
    setLoading(true);
    try {
      const schedule: Schedule = { frequency: 'monthly' };
      const stDate = Number(statementDate) || 1;
      schedule.date1 = (stDate + 21) % 30 || 30;

      const cardData = {
        userId,
        householdId,
        type: 'credit_card',
        name,
        totalAmount: Number(totalAmount),
        currentAmount: Number(currentAmount) || 0,
        monthlyMinimum: Number(monthlyMinimum),
        schedule,
        statementDate: Number(statementDate),
        autoCalculateMin: true,
        interestRate: Number(interestRate)
      };

      if (editingCard?.id) {
        await updateDoc(doc(db, 'debts', editingCard.id), cardData);
      } else {
        await addDoc(collection(db, 'debts'), {
          ...cardData,
          createdAt: serverTimestamp()
        });
      }
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold mb-6 text-slate-900">{editingCard ? 'Edit Credit Card' : 'Add Credit Card'}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Card Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="e.g. Visa Credit Card"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Credit Limit</label>
              <input
                type="number"
                step="0.01"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Current Balance (Owed)</label>
              <input
                type="number"
                step="0.01"
                value={currentAmount}
                onChange={(e) => setCurrentAmount(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Monthly Minimum</label>
              <input
                type="number"
                step="0.01"
                value={monthlyMinimum}
                readOnly
                className="w-full px-4 py-2 bg-blue-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm text-blue-700 cursor-not-allowed"
                placeholder="0.00"
              />
              <p className="text-[10px] text-blue-600 mt-1">Auto-calculated</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Interest Rate (%)</label>
              <input
                type="number"
                step="0.01"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="21.99"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Statement Release Date (1-31)</label>
            <input
              type="number" min="1" max="31"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-1"
              required
            />
            <p className="text-xs text-slate-500 mt-1">
              Estimated Due Date: <strong>{((Number(statementDate) || 1) + 21) % 30 || 30}th</strong> of the month.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-rose-600 text-white rounded-md font-semibold text-sm hover:bg-rose-700 disabled:opacity-50 mt-4 shadow-sm transition-colors"
          >
            {loading ? 'Saving...' : (editingCard ? 'Save Changes' : 'Add Credit Card')}
          </button>
        </form>
      </div>
    </div>
  );
}
